import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, subMonths, addMonths, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Copy,
  Pencil,
  Check,
  X,
  RotateCcw,
  TrendingUp,
  Building2,
  Lightbulb,
  Truck,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import DashboardLayout from "../components/DashboardLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TYPE_LABELS = {
  structural: { label: "Estructural", icon: Building2, color: "bg-slate-100 text-slate-700" },
  strategic: { label: "Estratégico", icon: Lightbulb, color: "bg-amber-50 text-amber-700" },
};

function fmt(n) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
}

function toYYYYMM(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ─── Inline-editable row ───────────────────────────────────────────────────
function CostRow({ item, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    category_name: item.category_name,
    amount: item.amount,
    type: item.type,
    is_recurring: item.is_recurring,
  });

  const handleSave = async () => {
    try {
      await onUpdate(item.id, form);
      setEditing(false);
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  const TypeIcon = TYPE_LABELS[form.type]?.icon || Building2;

  if (editing) {
    return (
      <tr className="bg-blue-50/40 border-b border-slate-100">
        <td className="px-4 py-2">
          <Input
            value={form.category_name}
            onChange={(e) => setForm((f) => ({ ...f, category_name: e.target.value }))}
            className="h-8 text-sm"
            data-testid="edit-category-name"
          />
        </td>
        <td className="px-4 py-2">
          <select
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            className="text-sm border border-slate-200 rounded-md px-2 py-1 bg-white h-8"
            data-testid="edit-type-select"
          >
            <option value="structural">Estructural</option>
            <option value="strategic">Estratégico</option>
          </select>
        </td>
        <td className="px-4 py-2">
          <Input
            type="number"
            min="0.01"
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
            className="h-8 text-sm w-32"
            data-testid="edit-amount"
          />
        </td>
        <td className="px-4 py-2 text-center">
          <input
            type="checkbox"
            checked={form.is_recurring}
            onChange={(e) => setForm((f) => ({ ...f, is_recurring: e.target.checked }))}
            className="w-4 h-4 accent-blue-600"
            data-testid="edit-recurring"
          />
        </td>
        <td className="px-4 py-2">
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600 hover:bg-green-50" onClick={handleSave} data-testid="save-edit-btn">
              <Check className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:bg-slate-100" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors group">
      <td className="px-4 py-3 text-sm font-medium text-slate-800">{item.category_name}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_LABELS[item.type]?.color}`}>
          <TypeIcon className="w-3 h-3" />
          {TYPE_LABELS[item.type]?.label}
        </span>
      </td>
      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{fmt(item.amount)}</td>
      <td className="px-4 py-3 text-center">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.is_recurring ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
          {item.is_recurring ? "Sí" : "No"}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-blue-600" onClick={() => setEditing(true)} data-testid={`edit-btn-${item.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-red-600" onClick={() => onDelete(item.id)} data-testid={`delete-btn-${item.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ─── Add cost form ─────────────────────────────────────────────────────────
function AddCostForm({ currentMonth, onAdd }) {
  const [form, setForm] = useState({ category_name: "", amount: "", type: "structural", is_recurring: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category_name.trim() || !form.amount) return;
    try {
      await onAdd({ ...form, amount: parseFloat(form.amount), month: currentMonth });
      setForm({ category_name: "", amount: "", type: "structural", is_recurring: false });
    } catch (e) {
      toast.error("Error al añadir coste");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap gap-2 items-end p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex-1 min-w-40">
        <label className="text-xs text-slate-500 mb-1 block">Categoría</label>
        <Input
          placeholder="Ej: Alquiler oficina"
          value={form.category_name}
          onChange={(e) => setForm((f) => ({ ...f, category_name: e.target.value }))}
          className="h-9 text-sm"
          required
          data-testid="new-category-name"
        />
      </div>
      <div className="w-36">
        <label className="text-xs text-slate-500 mb-1 block">Tipo</label>
        <select
          value={form.type}
          onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          className="w-full text-sm border border-slate-200 rounded-md px-2 py-2 bg-white h-9"
          data-testid="new-type-select"
        >
          <option value="structural">Estructural</option>
          <option value="strategic">Estratégico</option>
        </select>
      </div>
      <div className="w-32">
        <label className="text-xs text-slate-500 mb-1 block">Importe (€)</label>
        <Input
          type="number"
          min="0.01"
          step="0.01"
          placeholder="0.00"
          value={form.amount}
          onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
          className="h-9 text-sm"
          required
          data-testid="new-amount"
        />
      </div>
      <div className="flex items-center gap-2 pb-0.5">
        <label className="text-xs text-slate-500">Recurrente</label>
        <input
          type="checkbox"
          checked={form.is_recurring}
          onChange={(e) => setForm((f) => ({ ...f, is_recurring: e.target.checked }))}
          className="w-4 h-4 accent-blue-600"
          data-testid="new-recurring"
        />
      </div>
      <Button type="submit" className="h-9 px-4 bg-slate-900 hover:bg-slate-700 text-white text-sm" data-testid="add-cost-btn">
        <Plus className="w-4 h-4 mr-1" /> Añadir
      </Button>
    </form>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────
export default function FixedCostsPage() {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);

  // Shipping cost state
  const [shippingData, setShippingData] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingEditing, setShippingEditing] = useState(false);
  const [shippingCopying, setShippingCopying] = useState(false);
  const [shippingForm, setShippingForm] = useState({ shipping_cost: "", notes: "" });

  const currentMonth = toYYYYMM(currentDate);
  const monthLabel = format(currentDate, "MMMM yyyy", { locale: es });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/fixed-costs?month=${currentMonth}`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      toast.error("Error cargando costes fijos");
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadShippingData = useCallback(async () => {
    setShippingLoading(true);
    try {
      const res = await axios.get(`${API}/api/shipping-costs?month=${currentMonth}`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      setShippingData(res.data);
      setShippingForm({
        shipping_cost: res.data.shipping_cost != null ? res.data.shipping_cost : "",
        notes: res.data.notes || "",
      });
    } catch (e) {
      setShippingData(null);
    } finally {
      setShippingLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => { loadShippingData(); }, [loadShippingData]);

  const handleAdd = async (payload) => {
    await axios.post(`${API}/api/fixed-costs`, payload, { headers: getHeaders(), withCredentials: true });
    toast.success("Coste añadido");
    loadData();
  };

  const handleUpdate = async (id, updates) => {
    await axios.put(`${API}/api/fixed-costs/${id}`, updates, { headers: getHeaders(), withCredentials: true });
    toast.success("Coste actualizado");
    loadData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este coste?")) return;
    await axios.delete(`${API}/api/fixed-costs/${id}`, { headers: getHeaders(), withCredentials: true });
    toast.success("Coste eliminado");
    loadData();
  };

  const handleCopyPrevious = async () => {
    setCopying(true);
    try {
      const res = await axios.post(
        `${API}/api/fixed-costs/copy-previous-month?target_month=${currentMonth}`,
        {},
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success(res.data.message);
      loadData();
    } catch (e) {
      toast.error("Error copiando costes");
    } finally {
      setCopying(false);
    }
  };

  const handleShippingSave = async () => {
    const cost = parseFloat(shippingForm.shipping_cost);
    if (isNaN(cost) || cost < 0) { toast.error("Importe inválido"); return; }
    setShippingLoading(true);
    try {
      await axios.put(
        `${API}/api/shipping-costs/${currentMonth}`,
        { shipping_cost: cost, notes: shippingForm.notes },
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success("Coste de envío guardado");
      setShippingEditing(false);
      loadShippingData();
    } catch (e) {
      toast.error("Error guardando coste de envío");
    } finally {
      setShippingLoading(false);
    }
  };

  const handleShippingCopyPrevious = async () => {
    setShippingCopying(true);
    try {
      const res = await axios.get(
        `${API}/api/shipping-costs/copy-previous?target_month=${currentMonth}`,
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success(`Copiado de ${res.data.copied_from}: ${fmt(res.data.shipping_cost)}`);
      loadShippingData();
    } catch (e) {
      if (e.response?.status === 404) toast.error("No hay coste de envío en el mes anterior");
      else toast.error("Error copiando coste de envío");
    } finally {
      setShippingCopying(false);
    }
  };

  const items = data?.items || [];
  const totals = data?.totals || { total: 0, structural: 0, strategic: 0, count: 0 };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Costes Fijos</h1>
            <p className="text-sm text-slate-500 mt-0.5">Control de estructura operativa mensual</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentDate((d) => subMonths(d, 1))}
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-semibold text-slate-700 capitalize min-w-[130px] text-center" data-testid="current-month-label">
              {monthLabel}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentDate((d) => addMonths(d, 1))}
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Totals summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border border-slate-200 bg-white" data-testid="total-fixed-costs-card">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Costes Fijos</p>
              <p className="text-2xl font-black text-slate-900">{fmt(totals.total)}</p>
              <p className="text-xs text-slate-400 mt-0.5">{totals.count} partidas</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 bg-white" data-testid="structural-costs-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-1 mb-1">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs text-slate-500 uppercase tracking-wider">Estructural</p>
              </div>
              <p className="text-2xl font-black text-slate-700">{fmt(totals.structural)}</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 bg-white" data-testid="strategic-costs-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-1 mb-1">
                <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
                <p className="text-xs text-slate-500 uppercase tracking-wider">Estratégico</p>
              </div>
              <p className="text-2xl font-black text-amber-700">{fmt(totals.strategic)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card className="border border-slate-200 bg-white">
          <CardHeader className="pb-0 pt-4 px-4 flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700">
              Partidas — {monthLabel}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 gap-1"
              onClick={handleCopyPrevious}
              disabled={copying}
              data-testid="copy-previous-month-btn"
            >
              <Copy className="w-3 h-3" />
              {copying ? "Copiando..." : "Copiar mes anterior"}
            </Button>
          </CardHeader>
          <CardContent className="p-0 mt-3">
            {loading ? (
              <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="fixed-costs-table">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/60">
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Categoría</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Importe</th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Recurrente</th>
                        <th className="px-4 py-2.5 w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">
                            No hay costes fijos para este mes.
                            <br />
                            <span className="text-xs">Añade partidas usando el formulario inferior.</span>
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => (
                          <CostRow
                            key={item.id}
                            item={item}
                            onUpdate={handleUpdate}
                            onDelete={handleDelete}
                          />
                        ))
                      )}
                    </tbody>
                    {items.length > 0 && (
                      <tfoot>
                        <tr className="border-t-2 border-slate-200 bg-slate-50/80">
                          <td className="px-4 py-3 text-sm font-bold text-slate-800" colSpan={2}>TOTAL</td>
                          <td className="px-4 py-3 text-sm font-black text-slate-900" data-testid="total-amount-footer">{fmt(totals.total)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {/* Add form */}
                <div className="p-4 border-t border-slate-100">
                  <AddCostForm currentMonth={currentMonth} onAdd={handleAdd} />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* ── Shipping Costs Section ──────────────────────────────────────── */}
        <Card className="border border-slate-200 bg-white" data-testid="shipping-costs-card">
          <CardHeader className="pb-0 pt-4 px-4 flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-blue-500" />
              <CardTitle className="text-sm font-semibold text-slate-700">
                Coste Real de Envío — {monthLabel}
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7 gap-1"
                onClick={handleShippingCopyPrevious}
                disabled={shippingCopying || shippingLoading}
                data-testid="shipping-copy-previous-btn"
              >
                <Copy className="w-3 h-3" />
                {shippingCopying ? "Copiando..." : "Copiar mes anterior"}
              </Button>
              {!shippingEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 gap-1"
                  onClick={() => setShippingEditing(true)}
                  data-testid="shipping-edit-btn"
                >
                  <Pencil className="w-3 h-3" /> Editar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 mt-2">
            {shippingLoading ? (
              <div className="text-sm text-slate-400 text-center py-4">Cargando...</div>
            ) : shippingEditing ? (
              <div className="space-y-3">
                <div className="flex gap-3 items-end">
                  <div className="w-44">
                    <label className="text-xs text-slate-500 mb-1 block">Coste pagado al carrier (€)</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={shippingForm.shipping_cost}
                      onChange={(e) => setShippingForm((f) => ({ ...f, shipping_cost: e.target.value }))}
                      className="h-9 text-sm"
                      data-testid="shipping-cost-input"
                      autoFocus
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 mb-1 block">Notas (opcional)</label>
                    <Input
                      placeholder="Ej: Factura Correos + DHL"
                      value={shippingForm.notes}
                      onChange={(e) => setShippingForm((f) => ({ ...f, notes: e.target.value }))}
                      className="h-9 text-sm"
                      data-testid="shipping-notes-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-8 px-4 bg-slate-900 hover:bg-slate-700 text-white text-sm"
                    onClick={handleShippingSave}
                    disabled={shippingLoading}
                    data-testid="shipping-save-btn"
                  >
                    <Check className="w-3 h-3 mr-1" /> Guardar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-sm"
                    onClick={() => {
                      setShippingEditing(false);
                      setShippingForm({
                        shipping_cost: shippingData?.shipping_cost != null ? shippingData.shipping_cost : "",
                        notes: shippingData?.notes || "",
                      });
                    }}
                    data-testid="shipping-cancel-btn"
                  >
                    <X className="w-3 h-3 mr-1" /> Cancelar
                  </Button>
                </div>
              </div>
            ) : shippingData?.shipping_cost != null ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-black text-blue-700" data-testid="shipping-cost-display">
                    {fmt(shippingData.shipping_cost)}
                  </p>
                  {shippingData.notes && (
                    <p className="text-xs text-slate-500 mt-1">{shippingData.notes}</p>
                  )}
                  {shippingData.updated_at && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      Actualizado: {new Date(shippingData.updated_at).toLocaleDateString("es-ES")}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="shipping-not-configured">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Coste de envío no configurado para este mes</p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Sin este dato, el margen de envío no será preciso. Haz clic en "Editar" para añadirlo.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
